import { emit } from 'src/emitter';
import {
  STATE_TOKEN_TYPE,
  STATE_TOKEN_TYPE_ACTIVE,
} from './token_type_modal_controller';
import {
  STATE_TOKEN_OPERATOR,
  STATE_TOKEN_OPERATOR_ACTIVE,
} from './token_operator_modal_controller';
import {
  STATE_TOKEN_VALUE,
  STATE_TOKEN_VALUE_ACTIVE,
} from './token_value_modal_controller';
import { SearchBarState, STATE_BLURRED } from './states';
import { TokenOperator, TokenType } from './tokens';

export const EVENT_SET_STATE = 'search-bar:state';
export const EVENT_SET_QUERY = 'search-bar:query';
export const EVENT_INPUT = 'search-bar:input';
export const EVENT_ESCAPE = 'search-bar:escape';
export const EVENT_PICK_VALUE = 'search-bar:token-value:pick';
export const EVENT_PICK_TYPE = 'search-bar:token-type:pick';
export const EVENT_PICK_OPERATOR = 'search-bar:token-operator:pick';
export const EVENT_CHANGED_MODAL_LAYOUT = 'search-bar:modal-layout:changed';

export function emitPickedType(type: string) {
  // Next frame to ensure that emitted events do not compete with current event
  // handlers. This is important because we don't want event handling to be
  // order dependent.
  setTimeout(
    () => emit(EVENT_PICK_TYPE, { type } satisfies EventTokenTypePickParams),
    1,
  );
}

export function emitPickedOperator(operator: string) {
  // Next frame to ensure that emitted events do not compete with current event
  // handlers. This is important because we don't want event handling to be
  // order dependent.
  setTimeout(
    () =>
      emit(EVENT_PICK_OPERATOR, {
        operator,
      } satisfies EventTokenOperatorPickParams),
    1,
  );
}

export function emitPickedValue(
  value: string,
  labelledValue?: string | Element,
) {
  let label = undefined;

  if (typeof labelledValue === 'string') {
    label = labelledValue;
  } else if (
    typeof labelledValue === 'object' &&
    labelledValue instanceof Element
  ) {
    label = `html:${labelledValue.outerHTML}`;
  }

  // Next frame to ensure that emitted events do not compete with current event
  // handlers. This is important because we don't want event handling to be
  // order dependent.
  setTimeout(() => {
    emit(EVENT_PICK_VALUE, {
      value,
      label,
    } satisfies EventTokenValuePickParams);
  });
}

export function emitInput(key: string) {
  emit(EVENT_INPUT, {
    key,
  } satisfies EventInputParams);
}

export function emitSetQuery(value: string) {
  emit(EVENT_SET_QUERY, {
    value,
  } satisfies EventSetQueryParams);
}

export function emitSetState<State extends SearchBarState>(
  state: State,
  params?: Omit<EventSetStateParamsMap[State], 'state'>,
) {
  emit(EVENT_SET_STATE, {
    state,
    ...params,
  });
}

export function emitChangedModalLayout() {
  emit(EVENT_CHANGED_MODAL_LAYOUT);
}

export type ActionPickTokenValue = Event & {
  params: EventTokenValuePickParams;
};

export type ActionPickTokenType = Event & {
  params: EventTokenTypePickParams;
};

export type ActionPickTokenOperator = Event & {
  params: EventTokenOperatorPickParams;
};

export type EventSetStateParamsMap = {
  [STATE_BLURRED]: { state: typeof STATE_BLURRED };
  [STATE_TOKEN_TYPE]: { state: typeof STATE_TOKEN_TYPE };
  [STATE_TOKEN_TYPE_ACTIVE]: { state: typeof STATE_TOKEN_TYPE_ACTIVE };
  [STATE_TOKEN_OPERATOR]: {
    state: typeof STATE_TOKEN_OPERATOR;
    type: TokenType;
  };
  [STATE_TOKEN_OPERATOR_ACTIVE]: {
    state: typeof STATE_TOKEN_OPERATOR_ACTIVE;
    type: TokenType;
  };
  [STATE_TOKEN_VALUE]: {
    state: typeof STATE_TOKEN_VALUE;
    type: TokenType;
    operator: TokenOperator;
    value?: string;
    label?: string;
  };
  [STATE_TOKEN_VALUE_ACTIVE]: {
    state: typeof STATE_TOKEN_VALUE_ACTIVE;
    type: TokenType;
    operator: TokenOperator;
    value?: string;
    label?: string;
  };
};

export type EventSetStateParams =
  EventSetStateParamsMap[keyof EventSetStateParamsMap];

export type EventInputParams = {
  key: string;
};

export type EventSetQueryParams = {
  value: string;
};

export type EventTokenValuePickParams = {
  value: string;
  label?: string;
};

export type EventTokenTypePickParams = {
  type: string;
};

export type EventTokenOperatorPickParams = {
  operator: string;
};

export function isEventSetState(event: unknown): event is EventSetStateParams {
  return Boolean(
    event &&
      typeof event === 'object' &&
      'state' in event &&
      typeof event.state === 'string',
  );
}

export function isEventSetQuery(event: unknown): event is EventSetQueryParams {
  return Boolean(
    event &&
      typeof event === 'object' &&
      'value' in event &&
      typeof event.value === 'string',
  );
}

export function isEventInput(event: unknown): event is EventInputParams {
  return Boolean(
    event &&
      typeof event === 'object' &&
      'key' in event &&
      typeof event.key === 'string',
  );
}

export function isEventPickValue(
  event: unknown,
): event is EventTokenValuePickParams {
  return Boolean(
    event &&
      typeof event === 'object' &&
      'value' in event &&
      typeof event.value === 'string',
  );
}

export function isActionPickValue(
  event: unknown,
): event is ActionPickTokenValue {
  if (!event || typeof event !== 'object' || !('params' in event)) {
    return false;
  }

  if (!event.params || typeof event.params !== 'object') {
    return false;
  }

  return 'value' in event.params;
}

export function isActionPickType(event: unknown): event is ActionPickTokenType {
  if (!event || typeof event !== 'object' || !('params' in event)) {
    return false;
  }

  if (!event.params || typeof event.params !== 'object') {
    return false;
  }

  return 'type' in event.params;
}

export function isActionPickOperator(
  event: unknown,
): event is ActionPickTokenOperator {
  if (!event || typeof event !== 'object' || !('params' in event)) {
    return false;
  }

  if (!event.params || typeof event.params !== 'object') {
    return false;
  }

  return 'operator' in event.params;
}
